import { ThemeEvents } from '@theme/events';

/**
 * Shows the product brand and style number.
 * The style number is the POS vendor style code when present (rendered in Liquid);
 * otherwise the variant SKU with any trailing "-{ring size}" removed, refreshed
 * whenever the selected variant changes.
 */
class BrandStyleNumber extends HTMLElement {
  connectedCallback() {
    this.target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    this.target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    this.target?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const newProduct = event.detail?.data?.newProduct;
    if (newProduct) this.dataset.productId = newProduct.id;

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) return;

    // Product-level vendor style code from the POS doesn't change per variant
    if (this.hasAttribute('data-fixed-style')) return;

    const variant = event.detail?.resource;
    if (!variant) return;

    const row = this.querySelector('[data-style-row]');
    const value = this.querySelector('[data-style-number]');
    if (!row || !value) return;

    const styleNumber = this.#styleNumber(variant);
    value.textContent = styleNumber;
    row.hidden = !styleNumber;
  };

  /**
   * @param {{ sku?: string, options?: string[] }} variant
   * @returns {string}
   */
  #styleNumber(variant) {
    const sku = (variant.sku || '').trim();
    const sizeIndex = Number(this.dataset.sizeOptionIndex);
    const size = sizeIndex >= 0 ? variant.options?.[sizeIndex] : null;
    const suffix = size ? `-${size}` : null;

    if (suffix && sku.length > suffix.length && sku.endsWith(suffix)) {
      return sku.slice(0, -suffix.length);
    }
    return sku;
  }
}

if (!customElements.get('brand-style-number')) {
  customElements.define('brand-style-number', BrandStyleNumber);
}
